import { useState, useEffect, useCallback, useMemo } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { showConfirm } from '../../lib/platform'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, Msg, StyledInput, PrivacyPicker, RowBtn } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import GradientButton from '../../components/GradientButton'

// ─── SezioneTeam ──────────────────────────────────────────────────────
export default function SezioneTeam({ leagueId, userId, teamRace, teamMember }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [myTeam, setMyTeam] = useState(null)
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [tourLocked, setTourLocked] = useState(false)
  const [msg, setMsg] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [showCrea, setShowCrea] = useState(false)
  const [newName, setNewName] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const [newPassword, setNewPassword] = useState('')
  const [showRename, setShowRename] = useState(false)
  const [renameName, setRenameName] = useState('')
  const [showMembers, setShowMembers] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [newPwd, setNewPwd] = useState('')
  const [showChangePwd, setShowChangePwd] = useState(false)
  const [joinPassTeam, setJoinPassTeam] = useState(null)
  const [joinPassInput, setJoinPassInput] = useState('')
  const [pendingInvites, setPendingInvites] = useState([])
  const [leagueMembers, setLeagueMembers] = useState([])
  const [expandedTeam, setExpandedTeam] = useState(null)

  const styles = useMemo(() => makeStyles(tc), [tc])
  const load = useCallback(async () => {
    setLoading(true)
    const [myRes, allRes, lockRes] = await Promise.all([
      supabase.rpc('fn_my_team', { p_id_user: userId, p_id_league: leagueId }),
      supabase.rpc('fn_league_teams', { p_id_league: leagueId }),
      supabase.rpc('fn_tour_in_progress', { p_id_league: leagueId }),
    ])
    setMyTeam(myRes.data?.[0] ?? null)
    setTeams(allRes.data ?? [])
    setTourLocked(lockRes.data === true)
    setLoading(false)
  }, [userId, leagueId])

  useEffect(() => { if (teamRace) load() }, [leagueId, teamRace, load])

  useEffect(() => {
    if (!showInvite || !myTeam) return
    let cancelled = false
    Promise.all([
      supabase.rpc('fn_team_pending_invites', { p_id_team: myTeam.id_league_team }),
      supabase.rpc('fn_league_members', { p_id_league: leagueId }),
    ]).then(([invRes, memRes]) => { if (cancelled) return; setPendingInvites(invRes.data ?? []); setLeagueMembers(memRes.data ?? []) })
    return () => { cancelled = true }
  }, [showInvite, myTeam, leagueId])

  if (!teamRace) return null
  const isCreator = myTeam?.created_by === userId

  const handleCrea = async () => {
    if (!newName.trim()) {
      setMsg({ type: 'error', text: t('league.err_name') })
        ; return
    }
    if (!isPublic && !newPassword.trim()) {
      setMsg({ type: 'error', text: t('league.err_password') })
        ; return
    }
    setActionLoading(true); setMsg(null)
    try {
      const { data, error } = await supabase.rpc('fn_create_league_team', { p_id_user: userId, p_id_league: leagueId, p_name: newName.trim(), p_is_public: isPublic, p_password: isPublic ? null : newPassword.trim() })
      if (error) throw error
      const res = data?.[0]
      if (!res?.id_league_team) {
        setMsg({
          type: 'error', text: res?.message ?? t('common.error_title')
        }); return
      }
      setMsg({ type: 'success', text: res.message }); setNewName(''); setIsPublic(true); setShowCrea(false); await load()
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setActionLoading(false) }
  }

  const handleJoin = async (team) => {
    if (!team.is_public && joinPassTeam?.id_league_team !== team.id_league_team) { setJoinPassTeam(team); setJoinPassInput(''); return }
    setActionLoading(true); setMsg(null)
    try {
      const { data, error } = await supabase.rpc('fn_join_league_team', { p_id_user: userId, p_id_league: leagueId, p_id_team: team.id_league_team, p_password: team.is_public ? null : joinPassInput })
      if (error) throw error
      const res = data?.[0]
      setMsg({ type: res?.success ? 'success' : 'error', text: res?.message ?? 'Errore' })
      if (res?.success) { setJoinPassTeam(null); await load() }
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setActionLoading(false) }
  }

  const handleLeave = async () => {
    if (!await showConfirm(t('league.confirm_team_leave'))) return
    setActionLoading(true); setMsg(null)
    try {
      const { data, error } = await supabase.rpc('fn_leave_league_team', { p_id_user: userId, p_id_league: leagueId })
      if (error) throw error
      const res = data?.[0]
      setMsg({ type: res?.success ? 'success' : 'error', text: res?.message ?? 'Errore' })
      if (res?.success) await load()
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setActionLoading(false) }
  }

  const handleKick = async (targetId, name) => {
    if (!await showConfirm(t('league.confirm_kick', { name }))) return
    setActionLoading(true)
    try {
      await supabase.rpc('fn_kick_team_member', { p_caller: userId, p_id_user: targetId, p_id_league: leagueId })
      await load()
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setActionLoading(false) }
  }

  const handleInvite = async (targetUserId) => {
    setActionLoading(true)
    try {
      const { data, error } = await supabase.rpc('fn_invite_team_member', { p_caller: userId, p_id_team: myTeam.id_league_team, p_id_user: targetUserId })
      if (error) throw error
      const res = data?.[0]
      if (res?.success) {
        // Notifica push via edge function
        const { data: { session } } = await supabase.auth.getSession()
        const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL ?? ''
        await fetch(`${supabaseUrl}/functions/v1/notify-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ target_user_id: targetUserId, title: `Invito team — ${myTeam.name}`, body: JSON.stringify({ id_team: myTeam.id_league_team }), type: 'team_invite' }),
        })
        const { data: inv } = await supabase.rpc('fn_team_pending_invites', { p_id_team: myTeam.id_league_team })
        setPendingInvites(inv ?? [])
        setMsg({ type: 'success', text: t('league.team_invite_sent') })
      } else { setMsg({ type: 'error', text: res?.message ?? 'Errore' }) }
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setActionLoading(false) }
  }

  const alreadyInvited = new Set(pendingInvites.map(i => i.id_user))
  const myTeamMemberIds = new Set(Array.isArray(myTeam?.members) ? myTeam.members.map(m => m.id_user) : [])
  const invitableMembers = leagueMembers.filter(m => m.id_user !== userId && !alreadyInvited.has(m.id_user) && !myTeamMemberIds.has(m.id_user))

  if (loading) return <Card><Text style={{ color: tc.textMuted, fontSize: 13 }}>{t('league.team_loading')}</Text></Card>

  return (
    <View style={{ marginTop: 4 }}>
      {/* Header sezione */}
      <View style={styles.teamSectionHeader}>
        <Text style={[styles.teamSectionTitle, { color: tc.text }]}>{t('league.team_section')}</Text>
        {!myTeam && !tourLocked && (
          <RowBtn onPress={() => setShowCrea(s => !s)} variant={showCrea ? 'neutral' : 'rosa'}>
            {showCrea ? t('league.team_cancel') : t('league.team_create_btn')}
          </RowBtn>
        )}
      </View>

      {msg && <Msg type={msg.type} text={msg.text} />}
      {tourLocked && <View style={styles.lockedBanner}><Text style={styles.lockedText}>{t('league.team_locked')}</Text></View>}

      {/* Form crea team */}
      {showCrea && !myTeam && (
        <Card style={{ marginBottom: 12 }}>
          <StyledInput value={newName} onChangeText={setNewName} placeholder={t('league.team_name_placeholder')} />
          <PrivacyPicker isPublic={isPublic} onSelect={setIsPublic} labelPublic={t('league.public')} labelPrivate={t('league.private')} />
          {!isPublic && <StyledInput value={newPassword} onChangeText={setNewPassword} placeholder={t('league.create_password_placeholder')} secureTextEntry />}
          <GradientButton onPress={handleCrea} loading={actionLoading}>{t('league.team_create')}</GradientButton>
        </Card>
      )}

      {/* Il mio team */}
      {myTeam && (
        <Card style={{ borderColor: tc.rosa, backgroundColor: tc.rosaSubtle, marginBottom: 12 }}>
          <View style={styles.myTeamHeader}>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <MaterialCommunityIcons name="trophy-outline" size={14} color={tc.rosaText} />
                <Text style={styles.myTeamName}>{myTeam.name}</Text>
                {!myTeam.is_public && <Ionicons name="lock-closed-outline" size={12} color={tc.rosaText} />}
              </View>
              <Text style={[styles.myTeamMeta, { color: tc.textMuted }]}>{t('league.team_members_count', { count: myTeam.member_count, max: teamMember })}</Text>
            </View>
            <View style={styles.myTeamActions}>
              {isCreator && !tourLocked && <RowBtn onPress={() => { setShowRename(s => !s); setRenameName(myTeam.name) }}><MaterialCommunityIcons name="pencil-outline" size={13} color={tc.textMuted} /></RowBtn>}
              {isCreator && !tourLocked && <RowBtn onPress={() => setShowChangePwd(s => !s)}><Ionicons name="key-outline" size={13} color={tc.textMuted} /></RowBtn>}
              {isCreator && <RowBtn onPress={() => setShowMembers(s => !s)}><Ionicons name="people-outline" size={13} color={tc.textMuted} /></RowBtn>}
              {isCreator && myTeam.member_count < teamMember && !tourLocked && (
                <RowBtn onPress={() => setShowInvite(s => !s)} variant="rosa">{t('league.team_invite_btn')}</RowBtn>
              )}
              {!tourLocked && <RowBtn onPress={handleLeave} disabled={actionLoading}>{t('league.team_leave')}</RowBtn>}
            </View>
          </View>

          {showRename && isCreator && (
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              <View style={{ flex: 1 }}><StyledInput value={renameName} onChangeText={setRenameName} placeholder={t('league.team_rename_placeholder')} style={{ marginBottom: 0 }} /></View>
              <GradientButton size="sm" onPress={async () => {
                setActionLoading(true)
                const { data } = await supabase.rpc('fn_rename_league_team', { p_id_user: userId, p_id_team: myTeam.id_league_team, p_new_name: renameName.trim() })
                const res = data?.[0]; setMsg({ type: res?.success ? 'success' : 'error', text: res?.message ?? 'Errore' })
                if (res?.success) { setShowRename(false); await load() }
                setActionLoading(false)
              }}>{t('league.team_save')}</GradientButton>
            </View>
          )}

          {showChangePwd && isCreator && (
            <View style={[styles.teamInnerSection, { borderTopColor: tc.rosaBorder }]}>
              <Text style={[styles.teamInnerLabel, { color: tc.textMuted }]}>{t('league.team_pwd_hint')}</Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}><StyledInput value={newPwd} onChangeText={setNewPwd} placeholder={t('league.team_newpwd_placeholder')}
                  secureTextEntry style={{ marginBottom: 0 }} /></View>
                <GradientButton size="sm" onPress={async () => {
                  await supabase.from('dim_league_team').update({ password: newPwd.trim() || null, is_public: !newPwd.trim() }).eq('id_league_team', myTeam.id_league_team)
                  setMsg({ type: 'success', text: t('league.team_pwd_saved') })
                    ; setShowChangePwd(false); setNewPwd(''); await load()
                }}>{t('league.team_save')}</GradientButton>
              </View>
            </View>
          )}

          {showMembers && isCreator && (
            <View style={[styles.teamInnerSection, { borderTopColor: tc.rosaBorder }]}>
              {(myTeam.members ?? []).map(m => (
                <View key={m.id_user} style={styles.memberInlineRow}>
                  <Text style={[styles.memberInlineName, { color: tc.text }]}>{m.display_name}</Text>
                  {m.id_user !== userId && <RowBtn onPress={() => handleKick(m.id_user, m.display_name)} variant="danger" disabled={actionLoading}>{t('league.remove')}</RowBtn>}
                </View>
              ))}
            </View>
          )}

          {showInvite && isCreator && (
            <View style={[styles.teamInnerSection, { borderTopColor: tc.rosaBorder }]}>
              {pendingInvites.length > 0 && (
                <View style={{ marginBottom: 12 }}>
                  <Text style={[styles.teamInnerLabel, { color: tc.textMuted }]}>{t('league.team_pending')}</Text>
                  {pendingInvites.map(inv => (
                    <View key={inv.id} style={[styles.memberInlineRow, { borderBottomWidth: 1, borderBottomColor: tc.border }]}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><MaterialCommunityIcons name="clock-outline" size={12} color={tc.textMuted} /><Text style={[styles.memberInlineName, { color: tc.textMuted }]}>{inv.display_name}</Text></View>
                      <RowBtn onPress={async () => {
                        await supabase.rpc('fn_cancel_team_invite', { p_caller: userId, p_id: inv.id })
                        const { data } = await supabase.rpc('fn_team_pending_invites', { p_id_team: myTeam.id_league_team })
                        setPendingInvites(data ?? [])
                      }}>{t('league.team_invite_cancel')}</RowBtn>
                    </View>
                  ))}
                </View>
              )}
              <Text style={[styles.teamInnerLabel, { color: tc.textMuted }]}>{t('league.team_invite_section')}</Text>
              {invitableMembers.length === 0
                ? <Text style={{ fontSize: 12, color: tc.textMuted }}>{t('league.team_no_invitable')}</Text>
                : invitableMembers.map(m => (
                  <View key={m.id_user} style={styles.memberInlineRow}>
                    <Text style={[styles.memberInlineName, { color: tc.text }]}>{m.display_name}</Text>
                    <GradientButton size="sm" onPress={() => handleInvite(m.id_user)} disabled={actionLoading}>{t('league.team_invite_action')}</GradientButton>
                  </View>
                ))
              }
            </View>
          )}
        </Card>
      )}

      {/* Password join team privato */}
      {joinPassTeam && (
        <Card style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 12, color: tc.rosaText, marginBottom: 8 }}>{t('league.team_private_label', { name: joinPassTeam.name })}</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <View style={{ flex: 1 }}><StyledInput value={joinPassInput} onChangeText={setJoinPassInput} placeholder={t('league.create_password_placeholder')}
              secureTextEntry style={{ marginBottom: 0 }} /></View>
            <GradientButton size="sm" onPress={() => handleJoin(joinPassTeam)} disabled={actionLoading}>{t('league.team_join_action')}</GradientButton>
            <RowBtn onPress={() => setJoinPassTeam(null)}><Ionicons name="close" size={13} color={tc.textMuted} /></RowBtn>
          </View>
        </Card>
      )}

      {/* Lista teams */}
      {teams.length === 0
        ? <Text style={{ fontSize: 13, color: tc.textMuted, textAlign: 'center', padding: 16 }}>{t('league.team_empty')}</Text>
        : (
          <View style={{ gap: 8 }}>
            {teams.map(team => {
              const isMyTeam = myTeam?.id_league_team === team.id_league_team
              const isFull = team.member_count >= teamMember
              const isExpanded = expandedTeam === team.id_league_team
              const membersList = Array.isArray(team.members)
                ? team.members.map(m => m.display_name ?? m).join(', ')
                : typeof team.members === 'string' ? team.members : ''
              return (
                <View key={team.id_league_team} style={[styles.teamRow, { backgroundColor: isMyTeam ? tc.rosaSubtle : tc.bgCard2, borderColor: isMyTeam ? tc.rosaBorder : tc.border }]}>
                  <View style={styles.teamRowHeader}>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.teamRowName, { color: tc.text }]}>{team.name}{!team.is_public && <Ionicons name="lock-closed-outline" size={11} color={tc.textMuted} style={{ marginLeft: 4 }} />}</Text>
                      <Text style={[styles.teamRowMeta, { color: tc.textMuted }]}>{t('league.team_members_count', { count: team.member_count, max: teamMember })}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                      <TouchableOpacity onPress={() => setExpandedTeam(isExpanded ? null : team.id_league_team)} style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }} accessibilityRole="button" accessibilityState={{ expanded: isExpanded }} accessibilityLabel={team.name ?? t('league.team_section')}>
                        <Text style={{ color: tc.textMuted, fontSize: 11 }}>{isExpanded ? <Ionicons name="chevron-up" size={14} color={tc.textMuted} /> : <Ionicons name="chevron-down" size={14} color={tc.textMuted} />}</Text>
                      </TouchableOpacity>
                      {isMyTeam && <Text style={styles.teamMineBadge}>{t('league.team_mine')}</Text>}
                      {!myTeam && !tourLocked && !isFull && <GradientButton size="sm" onPress={() => handleJoin(team)} disabled={actionLoading}>{t('league.team_join')}</GradientButton>}
                      {!myTeam && !tourLocked && isFull && <Text style={{ fontSize: 12, color: tc.textMuted }}>{t('league.team_full')}</Text>}
                    </View>
                  </View>
                  {isExpanded && membersList && (
                    <View style={[styles.teamExpandedBody, { borderTopColor: tc.border }]}>
                      <Text style={[styles.teamInnerLabel, { color: tc.textMuted, marginBottom: 6 }]}>{t('league.team_members')}</Text>
                      {membersList.split(', ').map((name, i) => (
                        <Text key={i} style={{ fontSize: 13, color: tc.text, paddingVertical: 3 }}>• {name}</Text>
                      ))}
                    </View>
                  )}
                </View>
              )
            })}
          </View>
        )
      }
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  teamSectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  teamSectionTitle: { fontFamily: fonts.condensedBold, fontSize: 15, textTransform: 'uppercase', letterSpacing: 1 },
  lockedBanner: { backgroundColor: tc.warningBg, borderWidth: 1, borderColor: tc.warningBorder, borderRadius: 10, padding: 10, paddingHorizontal: 14, marginBottom: 12 },
  lockedText: { fontSize: 12, color: tc.warning },
  myTeamHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  myTeamName: { fontSize: 15, color: tc.rosaText, fontFamily: fonts.condensedBold },
  myTeamMeta: { fontSize: 12, marginTop: 3 },
  myTeamActions: { flexDirection: 'row', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' },
  teamInnerSection: { marginTop: 10, paddingTop: 10, borderTopWidth: 1 },
  teamInnerLabel: { fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  memberInlineRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
  memberInlineName: { fontSize: 13 },
  teamRow: { borderRadius: 12, borderWidth: 1, overflow: 'hidden' },
  teamRowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, paddingHorizontal: 14 },
  teamRowName: { fontWeight: '600', fontSize: 14 },
  teamRowMeta: { fontSize: 12, marginTop: 2 },
  teamMineBadge: { fontSize: 12, color: tc.rosaText, fontWeight: '800' },
  teamExpandedBody: { padding: 8, paddingHorizontal: 14, paddingBottom: 12, borderTopWidth: 1 },
})